import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import * as satellite from 'satellite.js'
import axios from '../plugins/axios'

// WGS84 Earth equatorial radius in kilometers, used for coverage calculation
const EARTH_RADIUS_KM = 6378.137;

export function useSatelliteTracking() {
  const satellites = ref([])
  const activeSatelliteNames = ref([])
  const focusSatellite = ref(null)
  const orbitPathPoints = ref([])
  let satelliteUpdateInterval = null

  function parseTleData(tleString) {
    const lines = tleString.trim().split('\n').map(line => line.trim());
    const tleMap = {};
    if (lines.length < 3) return tleMap;

    for (let i = 0; i <= lines.length - 3; i += 3) {
      const name = lines[i];
      const line1 = lines[i + 1];
      const line2 = lines[i + 2];
      
      if (name && line1 && line1.startsWith('1 ') && line2 && line2.startsWith('2 ')) {
        tleMap[name.toUpperCase()] = { line1, line2 };
      }
    }
    return tleMap;
  }

  async function initializeSatellitesAndStations() {
    try {
      const [satellitesResponse, tleResponse] = await Promise.all([
        axios.get(`/v1/satellitesWorldmap`),
        axios.get('https://api.tinygs.com/v1/tles.txt')
      ]);

      const baseSatellites = satellitesResponse.data;
      const tleData = parseTleData(tleResponse.data);

      const now = new Date();
      const gmst = satellite.gstime(now);

      satellites.value = baseSatellites.map(sat => {
        const tleKey = sat.displayName.toUpperCase();
        const tle = tleData[tleKey];

        if (tle && tle.line1 && tle.line2) {
          try {
            const satrec = satellite.twoline2satrec(tle.line1, tle.line2);
            let initialLat = sat.lat || 0;
            let initialLng = sat.lng || 0;
            let altitude = 0;
            let speed = 0;

            const positionAndVelocity = satellite.propagate(satrec, now);
            if (positionAndVelocity && 
                typeof positionAndVelocity.position === 'object' && positionAndVelocity.position !== null &&
                typeof positionAndVelocity.velocity === 'object' && positionAndVelocity.velocity !== null) {

              const positionEci = positionAndVelocity.position;
              const velocityEci = positionAndVelocity.velocity;

              const positionGd = satellite.eciToGeodetic(positionEci, gmst);
              initialLng = satellite.degreesLong(positionGd.longitude);
              initialLat = satellite.degreesLat(positionGd.latitude);
              altitude = positionGd.height;
              speed = Math.sqrt(Math.pow(velocityEci.x, 2) + Math.pow(velocityEci.y, 2) + Math.pow(velocityEci.z, 2));
            }
            
            return { ...sat, tleLine1: tle.line1, tleLine2: tle.line2, satrec, lat: initialLat, lng: initialLng, altitude, speed };
          } catch (e) {
            console.error(`Error processing TLE for ${sat.name}:`, e);
            return { ...sat, lat: sat.lat || 0, lng: sat.lng || 0, altitude: 0, speed: 0, satrec: null };
          }
        }
        return { ...sat, lat: sat.lat || 0, lng: sat.lng || 0, altitude: 0, speed: 0, satrec: null };
      }).filter(Boolean);

    } catch (error) {
      console.error("Error initializing satellite data:", error);
      satellites.value = [];
    }
  }

  function updateAllSatellitePositions() {
    const now = new Date();
    const gmst = satellite.gstime(now);

    satellites.value = satellites.value.map(sat => {
      if (sat.satrec) {
        try {
          const positionAndVelocity = satellite.propagate(sat.satrec, now);
          if (positionAndVelocity && 
              typeof positionAndVelocity.position === 'object' && positionAndVelocity.position !== null &&
              typeof positionAndVelocity.velocity === 'object' && positionAndVelocity.velocity !== null) {

            const positionEci = positionAndVelocity.position;
            const velocityEci = positionAndVelocity.velocity;

            const positionGd = satellite.eciToGeodetic(positionEci, gmst);
            const newLat = satellite.degreesLat(positionGd.latitude);
            const newLng = satellite.degreesLong(positionGd.longitude);
            const newAltitude = positionGd.height;
            const newSpeed = Math.sqrt(Math.pow(velocityEci.x, 2) + Math.pow(velocityEci.y, 2) + Math.pow(velocityEci.z, 2));

            return { ...sat, lat: newLat, lng: newLng, altitude: newAltitude, speed: newSpeed };
          }
          return sat;
        } catch (e) {
          console.error(`Error propagating satellite ${sat.name}:`, e);
          return sat;
        }
      }
      return sat;
    });
  }

  function calculateOrbitPathForSatellite(satelliteObject) {
    if (!satelliteObject || !satelliteObject.satrec || satelliteObject.satrec.error) {
      orbitPathPoints.value = [];
      return;
    }

    const segments = [];
    let currentSegment = [];
    const now = new Date();
    const totalDurationMinutes = 190;
    const stepMinutes = 1;

    for (let i = 0; i <= totalDurationMinutes; i += stepMinutes) {
      const time = new Date(now.getTime() + i * 60000);
      try {
        const positionAndVelocity = satellite.propagate(satelliteObject.satrec, time);

        if (!positionAndVelocity || !positionAndVelocity.position) {
          break;
        }

        const gmst = satellite.gstime(time);
        const positionGd = satellite.eciToGeodetic(positionAndVelocity.position, gmst);

        if (positionGd === false || 
            typeof positionGd.latitude !== 'number' || isNaN(positionGd.latitude) ||
            typeof positionGd.longitude !== 'number' || isNaN(positionGd.longitude)) {
          break;
        }

        const lat = satellite.degreesLat(positionGd.latitude);
        const lng = satellite.degreesLong(positionGd.longitude);

        if (isNaN(lat) || isNaN(lng)) {
          break;
        }

        if (currentSegment.length > 0) {
          const prevLng = currentSegment[currentSegment.length - 1][1];
          if (Math.abs(lng - prevLng) > 180)
            segments.push(currentSegment), currentSegment = [];
        }
        currentSegment.push([lat, lng]);

      } catch (e) {
        break;
      }
    }
    
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }
    orbitPathPoints.value = segments;
  }

  function calculateCoverageRadiusMeters(altitudeKm) {
    if (!altitudeKm || altitudeKm <= 0) return 0;
    
    if (EARTH_RADIUS_KM + altitudeKm <= 0) return 0;

    const cosAlpha = EARTH_RADIUS_KM / (EARTH_RADIUS_KM + altitudeKm);
    if (cosAlpha > 1 || cosAlpha < -1) return 0;

    const alphaRadians = Math.acos(cosAlpha);
    const radiusKm = EARTH_RADIUS_KM * alphaRadians;
    return radiusKm * 1000;
  }

  const focusedSatelliteObject = computed(() => {
    if (!focusSatellite.value || !satellites.value) {
      return null;
    }
    return satellites.value.find(s => s.name === focusSatellite.value);
  });

  const coverageCircleData = computed(() => {
    const sat = focusedSatelliteObject.value;
    if (sat && typeof sat.lat === 'number' && typeof sat.lng === 'number' && typeof sat.altitude === 'number') {
      const radiusMeters = calculateCoverageRadiusMeters(sat.altitude);

      if (radiusMeters > 0) {
        return {
          visible: true,
          center: [sat.lat, sat.lng],
          radius: radiusMeters,
          color: 'rgba(0, 120, 255, 0.7)',
          fillColor: 'rgba(0, 120, 255, 0.7)',
          fillOpacity: 0.1,
          weight: 1,
        };
      }
    }
    return { visible: false };
  });

  onMounted(() => {
    initializeSatellitesAndStations().then(() => {
      if (satelliteUpdateInterval) clearInterval(satelliteUpdateInterval);
      satelliteUpdateInterval = setInterval(updateAllSatellitePositions, 5000);
    });
  });

  onBeforeUnmount(() => {
    if (satelliteUpdateInterval) {
      clearInterval(satelliteUpdateInterval);
      satelliteUpdateInterval = null;
    }
  });

  return {
    // State
    satellites,
    activeSatelliteNames,
    focusSatellite,
    orbitPathPoints,
    
    // Computed
    focusedSatelliteObject,
    coverageCircleData,
    
    // Methods
    calculateOrbitPathForSatellite,
    calculateCoverageRadiusMeters,
    updateAllSatellitePositions,
    initializeSatellitesAndStations
  }
}